import LocalScheme from '@nuxtjs/auth/lib/schemes/local'
import axios from 'axios'
import Cookies from 'universal-cookie'
import {
  processAuthHeaders,
  setEnableSeamlessAuth,
} from '~/adapters/http/utils'
import {
  clearAuthArtifacts,
  generateExpiredAtDate,
  setSsoSession,
} from '~/utils/auth'
import { storeUserLoggedInLocalStorage } from '~/utils/general'
import { setStorage } from '~/utils/storage'
import { EventBus } from '@/plugins/event-bus'

const cookie = new Cookies()

export default class HubAuthScheme extends LocalScheme {
  reset() {
    if (this.options.tokenRequired) {
      this._clearToken()
    }

    // null, not false. nuxt-auth treats only undefined/null as unset, so `false`
    // makes it write the string "false" into the cookie and localStorage
    // instead of removing the keys.
    this.$auth.setUser(null)
    this.$auth.setToken(this.name, null)
    this.$auth.setRefreshToken(this.name, null)

    return Promise.resolve()
  }

  async logout(params) {
    try {
      // Only call sign_out when it's configured and there is a hub token to
      // revoke. Without a token the request cannot identify the session, so it
      // is skipped — but the teardown in `finally` still runs, which is what
      // makes it safe for callers to sign out unconditionally.
      if (this.options.endpoints.logout && params?.token) {
        // Pass `data` per call — assigning onto this.options.endpoints.logout
        // mutates the shared scheme config for every later request.
        await this.$auth.request(
          params,
          { ...this.options.endpoints.logout, data: params },
          true
        )
      }
    } catch (error) {
      // Report but don't rethrow: the local teardown below still runs, and
      // rethrowing would strand the caller before its redirect, leaving the
      // user on an authenticated-looking page with no credentials.
      this.$auth.ctx?.app?.$rollbar?.error?.(error)
    } finally {
      // Local teardown must run even when sign_out fails, otherwise a network
      // error leaves the session credentials in the browser.
      clearAuthArtifacts()
    }

    return this.$auth.reset()
  }

  getWebGLFingerprint() {
    try {
      // Create a canvas element
      const canvas = document.createElement('canvas')
      const gl =
        canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
      if (!gl) {
        return 'WebGL not supported'
      }
      // Get WebGL parameters
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
      const renderer =
        debugInfo && 'UNMASKED_RENDERER_WEBGL' in debugInfo
          ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
          : 'Unknown'
      const vendor =
        debugInfo && 'UNMASKED_VENDOR_WEBGL' in debugInfo
          ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
          : 'Unknown'
      const version = gl.getParameter(gl.VERSION)
      const shadingLanguageVersion = gl.getParameter(
        gl.SHADING_LANGUAGE_VERSION
      )
      const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE)
      const maxCubeMapTextureSize = gl.getParameter(
        gl.MAX_CUBE_MAP_TEXTURE_SIZE
      )
      const maxRenderbufferSize = gl.getParameter(gl.MAX_RENDERBUFFER_SIZE)
      // Create a fingerprint string
      const fingerprint = [
        'Renderer: ' + renderer,
        'Vendor: ' + vendor,
        'Version: ' + version,
        'Shading Language Version: ' + shadingLanguageVersion,
        'Max Texture Size: ' + maxTextureSize,
        'Max Cube Map Texture Size: ' + maxCubeMapTextureSize,
        'Max Renderbuffer Size: ' + maxRenderbufferSize,
      ].join('\n')
      return fingerprint
    } catch (e) {
      return 'Error: ' + (e.message || 'Unknown error')
    }
  }

  async login(payload) {
    if (!this.options.endpoints.login) {
      return
    }

    const crmDeviceId = this.getWebGLFingerprint()

    const requestBody = {
      data: {
        ...payload.data,
        is_direct: !!payload.data.is_direct,
        is_sso: !!payload.data.sso,
        crm_device_id: crmDeviceId,
      },
    }

    // Ditch any leftover local tokens before attempting to log in
    await this.$auth.reset()

    const { response, result } = await this.$auth.request(
      requestBody,
      this.options.endpoints.login,
      true
    )

    if (this.options.tokenRequired) {
      const token = this.options.tokenType
        ? this.options.tokenType + ' ' + result.data.access_token
        : result.data.access_token
      const refreshToken = response.data.data.refresh_token

      // setToken & setRefreshToken & Expired at
      this.$auth.setToken(this.name, token)
      if (this.options.refreshToken)
        this.$auth.setRefreshToken(this.name, refreshToken)
      // Expired at with timestamp
      const createdAt = response.data?.data?.created_at ?? 0
      const expiresIn = response.data?.data?.expires_in ?? 0
      const expiredAtTimestamp = (createdAt + expiresIn) * 1000

      // Ensure maxAge is always a valid positive integer
      let maxAgeInSeconds = Math.floor((expiredAtTimestamp - Date.now()) / 1000)
      if (!Number.isFinite(maxAgeInSeconds) || maxAgeInSeconds < 1) {
        maxAgeInSeconds = 3600 // Default to 1 hour if calculation invalid
      }

      const expiredAtTimeStampOneMonth = (createdAt || 0) + 2592000 // 30 days in seconds
      let maxAgeInSecondsOneMonth = Math.floor(
        (expiredAtTimeStampOneMonth * 1000 - Date.now()) / 1000
      )
      if (
        !Number.isFinite(maxAgeInSecondsOneMonth) ||
        maxAgeInSecondsOneMonth < 1
      ) {
        maxAgeInSecondsOneMonth = 2592000 // Default to 30 days
      }

      // Set token to cookies with maxAge
      setStorage('qontak._token.hub', token, {
        maxAge: maxAgeInSeconds,
      })
      // Set refresh token to cookies with maxAge
      setStorage('qontak._refresh_token.hub', refreshToken, {
        maxAge: maxAgeInSecondsOneMonth,
      })

      // Set valid until to cookies with maxAge
      const expiredAtDate = new Date(expiredAtTimestamp).toISOString()
      setStorage('qontak._token.hub.valid_until', expiredAtDate, {
        maxAge: maxAgeInSeconds,
      })

      // Set temporary token to cookies with different name
      setStorage('qontak._token.hub.share', token, {
        domain: process.env.HOST_URL,
        maxAge: 3600,
      })
      setSsoSession({
        token,
        refreshToken,
        expiredAt: expiredAtDate,
        maxAgeInSeconds,
        maxAgeInSecondsOneMonth,
        crmTokenAttr: result.data.crm_token_attr,
      })
      // Set time to refresh token to local storage
      if (process.env.NODE_ENV !== 'development') {
        localStorage.setItem('time_to_refresh', expiredAtTimestamp - 600000) // calculation based on expired time - 10 minute => store in miliseconds
      }
    }

    if (!requestBody?.data?.is_sso && !requestBody?.data?.is_direct) {
      await this.fetchUser()
      storeUserLoggedInLocalStorage(this.$auth.user.email)
    }

    return response
  }

  async refresh(payload) {
    let url = `${process.env.HUB_SERVICE_URL}/oauth/token`
    if (payload.data.is_sso) {
      url = `${process.env.HUB_SERVICE_URL}/sso_oauth/token`.replace(
        /([^:]\/)\/+/g,
        '$1'
      )
    }
    this.options.endpoints.refresh.url = url

    const { response, result } = await this.$auth.request(
      payload,
      this.options.endpoints.refresh,
      true
    )

    if (this.options.tokenRequired) {
      const token = this.options.tokenType
        ? this.options.tokenType + ' ' + result.data.access_token
        : result.data.access_token

      this.$auth.setToken(this.name, token)

      // Calculate maxAge for new token
      const createdAtRefresh = response.data?.data?.created_at ?? 0
      const expiresInRefresh = response.data?.data?.expires_in ?? 0
      const expiredAtTimestamp = (createdAtRefresh + expiresInRefresh) * 1000

      let maxAgeInSeconds = Math.floor((expiredAtTimestamp - Date.now()) / 1000)
      if (!Number.isFinite(maxAgeInSeconds) || maxAgeInSeconds < 1) {
        maxAgeInSeconds = 3600
      }

      const expiredAtTimeStampOneMonth = (createdAtRefresh || 0) + 2592000
      let maxAgeInSecondsOneMonth = Math.floor(
        (expiredAtTimeStampOneMonth * 1000 - Date.now()) / 1000
      )
      if (
        !Number.isFinite(maxAgeInSecondsOneMonth) ||
        maxAgeInSecondsOneMonth < 1
      ) {
        maxAgeInSecondsOneMonth = 2592000
      }

      // Set token to cookies with maxAge
      setStorage('qontak._token.hub', token, {
        maxAge: maxAgeInSeconds,
      })

      // this._setToken(response)

      if (this.options.refreshToken) {
        const refreshToken = response.data.data.refresh_token
        this.$auth.setRefreshToken(this.name, refreshToken)
        // Set refresh token to cookies with maxAge
        setStorage('qontak._refresh_token.hub', refreshToken, {
          maxAge: maxAgeInSecondsOneMonth,
        })

        const expiredAtDate = generateExpiredAtDate(response.data?.data)

        // Set valid until to cookies with maxAge
        setStorage('qontak._token.hub.valid_until', expiredAtDate, {
          maxAge: maxAgeInSeconds,
        })

        setSsoSession({
          token,
          refreshToken,
          expiredAt: expiredAtDate,
          maxAgeInSeconds,
          maxAgeInSecondsOneMonth,
          crmTokenAttr: result.data?.crm_token_attr,
        })
      }

      const responseAccount = await this.$auth.requestWith(
        this.name,
        this.options.endpoints.user
      )
      this.$auth.setUser(responseAccount)
      EventBus.$emit('re-initialize-mqtt')
    }
  }

  async fetchUser() {
    const cookieObj = new URLSearchParams(
      document.cookie.replaceAll('&', '%26').replaceAll('; ', '&')
    )
    const validTokenRefresh = cookieObj.get('qontak._refresh_token.hub')

    // User endpoint is disabled.
    if (!this.options.endpoints.user) {
      this.$auth.setUser({})
      return
    }

    let isUsingSSO = false
    let noToken = false
    let seamlessAuth = false
    await axios
      .get(`${process.env.HUB_SERVICE_URL}/api/core/v1/client_configs/config`)
      .then(({ data: response }) => {
        const { data } = response
        seamlessAuth = data?.seamless_auth || false
        const seamlessAuthFirst = data?.seamless_auth_first || false
        if (seamlessAuthFirst) {
          const chatSsoToken = cookie.get('chat_sso_token', {
            domain: process.env.HOST_URL,
          })
          const chatSsoRefreshToken = cookie.get('chat_sso_refresh_token', {
            domain: process.env.HOST_URL,
          })
          const oldChatToken = cookie.get('qontak._token.hub')
          const oldChatTokenEmpty = !oldChatToken || oldChatToken === 'false'

          if (oldChatTokenEmpty && chatSsoToken && chatSsoRefreshToken) {
            // Get maxAge from valid_until cookie if available
            const validUntil = cookie.get('chat_sso_valid_until', {
              domain: process.env.HOST_URL,
            })
            let maxAgeInSeconds = 86400 // Default 24 hours
            if (validUntil) {
              const calculated = Math.floor(
                (new Date(validUntil).getTime() - Date.now()) / 1000
              )
              if (Number.isFinite(calculated) && calculated > 0) {
                maxAgeInSeconds = calculated
              }
            }

            setStorage('qontak._token.hub', chatSsoToken, {
              maxAge: maxAgeInSeconds,
            })
            setStorage('qontak._refresh_token.hub', chatSsoRefreshToken, {
              maxAge: maxAgeInSeconds,
            })
            this.$auth.setToken(this.name, chatSsoToken)
            this.$auth.setRefreshToken(this.name, chatSsoRefreshToken)
          }
        }

        // Token is required but not available
        if (this.options.tokenRequired && !this.$auth.getToken(this.name)) {
          noToken = true
          return
        }

        setEnableSeamlessAuth(seamlessAuth)
        isUsingSSO =
          process.env.NODE_ENV === 'development'
            ? false
            : data?.sso?.is_using_parameter || data?.sso?.status
      })

    if (noToken) {
      return
    }

    try {
      const config = {
        headers: {},
      }
      processAuthHeaders(config.headers)
      const response = await this.$auth.requestWith(
        this.name,
        config,
        this.options.endpoints.user
      )
      this.$auth.setUser(response)
    } catch (error) {
      if (isUsingSSO) {
        const chatSsoRefreshToken = cookie.get('chat_sso_refresh_token', {
          path: '/',
          domain: process.env.HOST_URL,
        })
        const refreshToken = seamlessAuth
          ? chatSsoRefreshToken
          : validTokenRefresh
        const refreshTokenPayload = {
          data: {
            refresh_token: refreshToken,
            grant_type: 'refresh_token',
            is_sso: isUsingSSO,
          },
        }
        await this.refresh(refreshTokenPayload)
      }
    }
  }
}
